import React, {useEffect, useMemo} from 'react';
import {ActionIcon, Group, rem} from "@mantine/core";
import {IconCaretDown, IconCaretUp} from "@tabler/icons-react";
import {
    ModuleIdentifier,
    useLowerOpportunityPriorityMutation,
    useLowerTransactionTeamPriorityMutation,
    useRaiseOpportunityPriorityMutation,
    useRaiseTransactionTeamPriorityMutation
} from "@repo/services";
import {
    useAgMutation,
    shouldDisableIncrementer,
    shouldDisableLowerPriority,
    shouldDisableRaisePriority, priorityToHealthData
} from '@repo/utils';
import {notifications} from "@mantine/notifications";
import PriorityToHealthLabel from './PriorityToHealthLabel';

interface RaiseLowerPriorityProps {
    priority: any;
    module: ModuleIdentifier;
    id: number | undefined;
    status: string | undefined;
}

const RaiseLowerPriorityInput = ({priority,module,id,status}:RaiseLowerPriorityProps) => {
    const raisePriorityHook = module === ModuleIdentifier.aimOpportunity
        ? useRaiseOpportunityPriorityMutation
        : useRaiseTransactionTeamPriorityMutation;
    const lowerPriorityHook = module === ModuleIdentifier.aimOpportunity
        ? useLowerOpportunityPriorityMutation
        : useLowerTransactionTeamPriorityMutation
    const {
        trigger: triggerRaisePriority,
        isLoading: raisePriorityIsLoading
    } = useAgMutation(raisePriorityHook as any,
        () => {
            notifications.show({
                title: "Raise Priority Successful",
                message: `You successfully changed the health of this ${module === ModuleIdentifier.aimOpportunity ? "opportunity" : "transaction team"}`
            })
        });
    const {
        trigger: triggerLowerPriority,
        isLoading: lowerPriorityIsLoading
    } = useAgMutation(lowerPriorityHook as any,
        () => {
            notifications.show({
                title: "Lower Priority Successful",
                message: `You successfully changed the health of this ${module === ModuleIdentifier.aimOpportunity ? "opportunity" : "transaction team"}`
            })
        });

    const disableRaise = useMemo(() => {
        let disable = false;
        const disableByPriority = shouldDisableRaisePriority(priority);
        const disableBoth = shouldDisableIncrementer(module, status || "");
        if (disableByPriority || disableBoth) disable = true;
        return disable;
    },[priority, module, status])

    const disableLower = useMemo(() => {
        let disable = false;
        const disableByPriority = shouldDisableLowerPriority(priority);
        const disableBoth = shouldDisableIncrementer(module, status || "");
        if (disableByPriority || disableBoth) disable = true;
        return disable;
    },[priority, module, status])

    const priorityToHealth = useMemo(() => {
        return priorityToHealthData.find((d) => d.value === priority);
    },[priority]);

    return (
        <>
           <Group>
               <ActionIcon.Group>
                   <ActionIcon w={'5px'} variant="default" size="lg" aria-label="Priority" style={{ background: priorityToHealth?.color, border: `1px solid ${priorityToHealth?.color}` }}></ActionIcon>
                   <ActionIcon pl={40} pr={40} variant="default" size="lg" aria-label="Priority">
                       {priorityToHealth?.label || ""}
                   </ActionIcon>

                   <ActionIcon
                       disabled={disableRaise}
                       loading={raisePriorityIsLoading}
                       onClick={() => triggerRaisePriority(id)}
                       variant="default"
                       size="lg"
                       aria-label="Raise Priority"
                   >
                       <IconCaretUp style={{ width: rem(20) }} stroke={1.5} />
                   </ActionIcon>

                   <ActionIcon
                       disabled={disableLower}
                       loading={lowerPriorityIsLoading}
                       onClick={() => triggerLowerPriority(id)}
                       variant="default"
                       size="lg"
                       aria-label="Lower Priority"
                   >
                       <IconCaretDown style={{ width: rem(20) }} stroke={1.5} />
                   </ActionIcon>
               </ActionIcon.Group>
           </Group>
        </>
    );
};

export default RaiseLowerPriorityInput;
